/** W82 Teil Web-2: Satz-Katalog des Geists und die neuen Texte der Startseite (plan-w82.md Abschnitt 6, E17), wörtlich und
 *  gleich in iOS `WeltSaetze.swift`. Sätze kommen als `SatzTeil[]`, Signalwörter tragen ihren Zustand. Zahlwörter Zwei bis
 *  Zwölf, ab 13 Ziffern; der Vorname ist das erste Wort von `nutzer.name`, ohne ihn entfällt „, V“. */
import type { SatzTeil } from "./blasenForm";
import type { Fassung, Praesenz } from "./einstellungen";
import type { Stopp } from "./darf";
import { datumText, erledigtText, fristTag } from "./datum";
import { heuteBerlin, tagMonat, tageBis, type OffenLage, dinge } from "./lage";

const ZAHLWORT = ["null", "eins", "zwei", "drei", "vier", "fünf", "sechs", "sieben", "acht", "neun", "zehn", "elf", "zwölf"];

/** Zahlwort bis zwölf, ab 13 Ziffern; `gross` am Satzanfang. */
export function zahlwort(n: number, gross = false): string {
  if (n > 12 || n < 0 || !Number.isInteger(n)) return String(n);
  const w = ZAHLWORT[n] as string;
  return gross ? w.charAt(0).toUpperCase() + w.slice(1) : w;
}

/** Vorname aus dem Konto: erstes Wort von `nutzer.name`; leer heißt ohne Anrede (FW5). */
export function vornameAus(name: string | null | undefined): string | null {
  const v = (name ?? "").trim().split(/\s+/)[0];
  return v ? v : null;
}

const WOCHENTAG = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
const MONAT = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

function wochentagNr(iso: string): number {
  const [j, m, t] = iso.split("-").map(Number);
  return new Date(Date.UTC(j ?? 1970, (m ?? 1) - 1, t ?? 1)).getUTCDay();
}

/** Kopfzeile: „Freitag, 16. Oktober“, der Wochentag betont. */
export function kopfDatum(heute: string): { tag: string; rest: string } {
  return { tag: WOCHENTAG[wochentagNr(heute)] as string, rest: `, ${Number(heute.slice(8, 10))}. ${MONAT[Number(heute.slice(5, 7)) - 1]}` };
}

/** „Do 22.10“: nur für künftige Tage (Kachel „Bald“, Blase unterwegs); W82 Web-5h-N2b-2 (b-HW23) ohne Endpunkt (datum.ts) */
export function wtTagMonat(iso: string): string {
  return datumText(iso, "wochentagKurz");
}

const anrede = (vorname: string | null) => (vorname ? `, ${vorname}` : "");
const UEBERFAELLIG: SatzTeil = { text: "überfällig", signal: "ueberfaellig", betont: true };

/** Satz im Kopf zur echten Lage. Die Fassung ändert den Wortlaut nicht (Parameter wie iOS). W82 Web-5h1 (F2 = A, D-626,
 *  Satzquelle Z20 bis Z22): bei gewählter Person (`fuer`, Vorname) steht ihr Name vorn, ohne Anrede („Für Ben stehen zwei
 *  Dinge an.“); der zweite Satz und das betonte Signalwort bleiben. */
export function satzKopf(lage: OffenLage, vorname: string | null, _fassung: Fassung, fuer?: string): SatzTeil[] {
  void _fassung;
  const n = dinge(lage);
  const v = anrede(vorname);
  if (n === 0) {
    if (fuer) return [{ text: `Für ${fuer} ist alles ` }, { text: "erledigt", signal: "erledigt", betont: true }, { text: ". Gerade steht nichts an." }];
    // W82 Web-11 Nacharbeit 2 (iv), JF-L15 (D-1772 F-W11-4 = A/A, DP-F1): ohne gewählte Person spricht der Kopf den Haushalt an
    return [{ text: "Bei euch ist alles " }, { text: "erledigt", signal: "erledigt", betont: true }, { text: ". Gerade steht nichts an." }];
  }
  if (n === 1) {
    // JF-L15 (DP-F2): „Bei euch steht eine Sache an.“; der zweite Satz bleibt
    const kopf: SatzTeil = { text: fuer ? `Für ${fuer} steht eine Sache an.` : "Bei euch steht eine Sache an." };
    if (lage.ueberfaellig) return [kopf, { text: " Sie ist " }, UEBERFAELLIG, { text: "." }];
    if (lage.pruefen) return [kopf, { text: " Ein Brief wartet darauf, dass du ihn prüfst." }];
    return [kopf, { text: " Sie ist bald fällig." }];
  }
  const kopf: SatzTeil = { text: fuer ? `Für ${fuer} stehen ${zahlwort(n)} Dinge an.` : `${zahlwort(n, true)} Dinge stehen an${v}.` };
  if (lage.ueberfaellig === 0) return [kopf, { text: " Nichts davon ist überfällig." }];
  if (lage.ueberfaellig === 1) return [kopf, { text: " Eins davon ist " }, UEBERFAELLIG, { text: "." }];
  return [kopf, { text: ` ${zahlwort(lage.ueberfaellig, true)} davon sind ` }, UEBERFAELLIG, { text: "." }];
}

/** W82 Web-5h2 (HW7, HW16): das Nächste der Lage für den Kopfsatz, eine Frist (Betrag, Absender, Fristwort) oder eine Aufgabe
 *  (Titel); `betrag` schon formatiert („48,90 €“). */
export type Naechstes = { datum: string; betrag: string | null; name: string | null; wort: string | null };

/** HW7: „Heute“, „Morgen“, „Übermorgen“, ab drei Tagen „Am Freitag“. */
export function tagWort(datum: string, heute: string): string {
  const t = tageBis(datum, heute);
  if (t <= 0) return "Heute";
  if (t === 1) return "Morgen";
  if (t === 2) return "Übermorgen";
  return `Am ${WOCHENTAG[wochentagNr(datum)]}`;
}

/** W82 Web-5h-N1 (Frage 62 = Fassung 2, D-677, Satzquelle Z70 bis Z73): Fristwort im Kopfsatz je Art, alle weiblich („endet die
 *  Widerspruchsfrist“); `FRISTWORT` bleibt das Wort der Fristzeilen. */
export const FRISTNAME: Record<string, string> = { zahlen: "Zahlungsfrist", widersprechen: "Widerspruchsfrist", kuendigen: "Kündigungsfrist", melden: "Meldefrist", sonstige: "Frist" };

/** Ab dieser Länge (Zeichen des ganzen Kopfsatzes, langer Absender) gilt der Satz des Bestands (Fassungsdatei Frage 62). */
export const KOPFSATZ_GRENZE = 110;

/** HW7 (Satzquelle Z70 bis Z73, Frage 62 = Fassung 2, D-677): zweiter Satz mit dem Nächsten. Frist mit Betrag (a, d) „Morgen sind
 *  48,90 € für Stadtwerke Musterstadt dran.“, Frist ohne Betrag (b) „Übermorgen endet die Widerspruchsfrist bei Stadtwerke
 *  Musterstadt.“, Aufgabe (c) „Morgen ist „Zählerstand melden“ dran.“; der Absender nur mit „für“ oder „bei“ (kein Artikel, kein
 *  Verb zu ihm: Geschlecht und Zahl sind unbekannt). Ohne Namen null (dann gilt der Satz des Bestands). */
export function satzNaechstes(n: Naechstes, heute: string): string | null {
  const name = n.name?.trim();
  if (!name) return null;
  const tag = tagWort(n.datum, heute);
  if (n.wort === null) return `${tag} ist „${name}“ dran.`;
  return n.betrag ? `${tag} sind ${n.betrag} für ${name} dran.` : `${tag} endet die ${n.wort} bei ${name}.`;
}

/** Z70 bis Z73: erster Satz, der Vorname vorn („Anna, drei Dinge warten auf dich.“, ohne ihn „Drei Dinge warten auf dich.“);
 *  bei einem Ding „Anna, eine Sache wartet auf dich.“ (Vorschlag, mit dem Architekten abzustimmen), ab 13 Ziffern. */
function kopfWarten(n: number, vorname: string | null): string {
  const sache = n === 1 ? "eine Sache wartet auf dich." : `${zahlwort(n)} Dinge warten auf dich.`;
  return vorname ? `${vorname}, ${sache}` : sache.charAt(0).toUpperCase() + sache.slice(1);
}

/** W82 Web-5h2 (HW7, HW16, Satzquelle Z70 bis Z76): Kopfsatz der Startseite ohne gewählte Person. Lebhaft und nichts überfällig:
 *  der zweite Satz nennt das Nächste (HW7, Fassung 2 seit 5h-N1: „Anna, drei Dinge warten auf dich. Morgen sind … dran.“). Ruhig, wenn der Knopf den wartenden Brief zeigt (`zeigtBrief`): zuerst der Brief,
 *  dann die übrigen Dinge („Ein Brief wartet auf dich, Web. Vier Fristen stehen an, nichts ist überfällig.“, HW16); zählen
 *  Aufgaben mit (`sachen`), heißen sie „Sache“ und „Dinge“ wie in den Antworten. Sonst der Satz des Bestands (`satzKopf`). */
export function satzKopfStart(
  lage: OffenLage,
  vorname: string | null,
  fassung: Fassung,
  zusatz: { heute: string; naechstes: Naechstes | null; zeigtBrief: boolean; sachen: boolean },
): SatzTeil[] {
  const v = anrede(vorname);
  const rest = lage.ueberfaellig + lage.bald;
  if (fassung === "ruhig" && zusatz.zeigtBrief && lage.pruefen > 0 && lage.ueberfaellig === 0) {
    const brief = lage.pruefen === 1 ? `Ein Brief wartet auf dich${v}.` : `${zahlwort(lage.pruefen, true)} Briefe warten auf dich${v}.`;
    if (rest === 0) return [{ text: brief }];
    const eins = zusatz.sachen ? "Eine Sache steht an" : "Eine Frist steht an";
    const mehr = `${zahlwort(rest, true)} ${zusatz.sachen ? "Dinge" : "Fristen"} stehen an`;
    return [{ text: `${brief} ${rest === 1 ? eins : mehr}, nichts ist überfällig.` }];
  }
  const zweiter = fassung === "lebhaft" && lage.ueberfaellig === 0 && lage.bald > 0 && zusatz.naechstes ? satzNaechstes(zusatz.naechstes, zusatz.heute) : null;
  if (zweiter) {
    const kopf = kopfWarten(dinge(lage), vorname);
    // langer Absender: ab KOPFSATZ_GRENZE Zeichen der Satz des Bestands
    if (kopf.length + 1 + zweiter.length < KOPFSATZ_GRENZE) return [{ text: kopf }, { text: ` ${zweiter}` }];
  }
  return satzKopf(lage, vorname, fassung);
}

/** Kopfzeile ohne Ich-Stimme bei „Keine Erklärungen“: „**3** Dinge offen“, „**1** Sache offen“, „Nichts offen“. */
export function zeileKopf(n: number): SatzTeil[] {
  if (n === 0) return [{ text: "Nichts offen" }];
  return [{ text: String(n), betont: true }, { text: n === 1 ? " Sache offen" : " Dinge offen" }];
}

/** Name der einen Stelle, wenn der Kopf mehrere Dinge nennt (W82 Web-5b, K21, entscheide D3). */
const KNOPF_STELLE: Record<Stopp, string> = { ueberfaellig: "Zeig mir das Überfällige", vorschlag: "Zeig mir den Brief", bald: "Zeig mir die nächste Frist" };
/** W82 Web-5f (BS29, Frage 41 = A, D-585), Kennung `knopf.zeigen.bald.aufgabe`: ist das nächste baldige Ding eine Aufgabe
 *  (K36), nennt der Knopf sie statt der Frist. Neu in der Satzquelle des Web; iOS-4c übernimmt ihn wortgleich. */
const KNOPF_NAECHSTE_AUFGABE = "Zeig mir die nächste Aufgabe";

/** Knopf im Kopf nach dem, was er zeigt (W82 Web-5b, K21, entscheide D3): mehrere Stellen „Zeig sie mir“; eine Stelle bei
 *  einer Sache „Zeig es mir“; eine Stelle bei mehreren Sachen nennt sie („Zeig mir das Überfällige“, „Zeig mir den Brief“,
 *  „Zeig mir die nächste Frist“). `dinge` ist die Zahl des Kopfsatzes; die Fassung ändert nur die Zahl der Stopps
 *  (`zeigenStopps`). Ohne Ziel kein Knopf. */
export function knopfZeigen(stopps: Stopp[], dinge: number, baldIstAufgabe = false): string | null {
  const [erste] = stopps;
  if (!erste) return null;
  if (stopps.length > 1) return "Zeig sie mir";
  if (dinge <= 1) return "Zeig es mir";
  // BS29: `baldIstAufgabe` sagt, ob der Rundgang bei „bald“ eine Aufgabe ansteuert (`stelleVon` der Startseite)
  return erste === "bald" && baldIstAufgabe ? KNOPF_NAECHSTE_AUFGABE : KNOPF_STELLE[erste];
}

/** W82 Web-5f (F1 = A, H3, Texttabelle des Auftrags 5f): leise Zeile der Startseite bei gewählter Person. */
export const ANSICHT_TEXTE = {
  zeile: (vorname: string) => `Ansicht: ${vorname}`,
  alle: "Alle zeigen",
} as const;

/** FW18 (Nachtrag des Auftraggebers 18.10., D-460), wörtlich nach dem Beispiel „Für Ben ist nichts überfällig.“: der Geist
 *  sagt bei gewählter Person ohne Überfälliges einen Satz, statt zu fliegen. */
export function satzNichtsUeberfaellig(vorname: string): SatzTeil[] {
  return [{ text: `Für ${vorname} ist nichts überfällig.` }];
}

/** W82 Web-5h2 (D-617 Punkt 3, Satzquelle Z30, Z31): Tipp auf „Bald“ oder „Zu prüfen“ bei gewählter Person ohne passende
 *  Zeile; er sagt es an seinem Platz wie Z19 (`satzNichtsUeberfaellig`). */
export function satzNichtsBald(vorname: string): SatzTeil[] {
  return [{ text: `Für ${vorname} ist nichts bald fällig.` }];
}
export function satzKeinBrief(vorname: string): SatzTeil[] {
  return [{ text: `Für ${vorname} wartet kein Brief.` }];
}

/** W82 Web-5h2 (BS04, BS05, Satzquelle Z32 bis Z34): Satz des Gaist auf der Seite „Systemstatus“ und die Prüfzeit
 *  („Zuletzt geprüft um 14:03:27.“, Uhrzeit dazwischen). */
export const SYSTEMSTATUS_SAETZE = {
  laeuft: "Alles läuft.",
  gestoert: "Ein Dienst ist gerade gestört.",
  geprueft: ["Zuletzt geprüft um ", "."],
} as const;

/** Blase unterwegs am Stopp: Überfälliges mit Tagen, Bald mit Tag, Vorschlag mit Frage. `datum` ist der Tag der Frist. */
export function satzUnterwegs(stopp: Stopp, datum: string | null, heute: string): SatzTeil[] {
  if (stopp === "vorschlag") return [{ text: "Diesen Brief habe ich eingeordnet. " }, { text: "Passt das so?", betont: true }];
  const t = datum ? tageBis(datum, heute) : 0;
  if (stopp === "ueberfaellig") return [{ text: t === -1 ? "Das ist seit gestern " : `Das ist seit ${-t} Tagen ` }, UEBERFAELLIG, { text: "." }];
  if (t <= 0 || !datum) return [{ text: "Das ist heute fällig." }];
  return [{ text: `Das ist bis ${wtTagMonat(datum)} fällig.` }];
}

/** Fuß der Blase unterwegs: „1 von 3“. */
export function schrittText(nr: number, von: number): string {
  return `${nr} von ${von}`;
}

/** Antwort nach Abhaken („erledigt“) oder „Passt“ („abgelegt“), Fristen und Briefe getrennt (W82 Web-5b, K17, entscheide
 *  D1): `rest.fristen` sind Überfällig plus Bald danach, `rest.briefe` Zu prüfen danach, beide wie die Kacheln danach;
 *  `briefe` null heißt, die Seite kennt die Briefe nicht (Reiter Fristen) und nennt nur Fristen. Ziffern, Einzahl und
 *  Mehrzahl nach der Zahl; gleich in iOS `WeltSaetze.swift`. K36 (R2 W82-Web-5b): zählen in Überfällig plus Bald Aufgaben mit
 *  (`rest.aufgaben`), heißen sie mit dem Wort des Kopfsatzes „Sache“ beziehungsweise „Dinge“ statt „Frist“. */
export function satzAntwort(art: "erledigt" | "abgelegt", rest: { fristen: number; briefe: number | null; aufgaben?: number }): SatzTeil[] {
  const kopf: SatzTeil = art === "erledigt" ? { text: "Erledigt.", signal: "erledigt", betont: true } : { text: "Abgelegt." };
  const f = Math.max(0, rest.fristen);
  const b = Math.max(0, rest.briefe ?? 0);
  const sachen = (rest.aufgaben ?? 0) > 0;
  const fristen = sachen ? (f === 1 ? "1 Sache steht an" : `${f} Dinge stehen an`) : f === 1 ? "1 Frist steht an" : `${f} Fristen stehen an`;
  const briefe = b === 1 ? "1 Brief wartet" : `${b} Briefe warten`;
  if (f > 0 && b > 0) return [kopf, { text: ` Noch ${fristen} und ${briefe}.` }];
  if (f > 0) return [kopf, { text: ` Noch ${fristen}.` }];
  if (b > 0) return [kopf, { text: ` Noch ${briefe}.` }];
  return [kopf, { text: rest.briefe === null ? " Gerade steht keine Frist mehr an." : " Gerade steht nichts mehr an." }];
}

/** Stimme über dem Vorschlag; bei „Keine Erklärungen“ sachlich ohne Ich-Stimme (W7). */
export function stimmeVorschlag(praesenz: Praesenz): SatzTeil[] {
  if (praesenz === "keine") return [{ text: "Vorschlag zur Einordnung" }];
  return [{ text: "Ich habe einen Brief eingeordnet. " }, { text: "Passt das so?", betont: true }];
}

/** W82 Web-5h3 (HW13, Satzquelle Z70 bis Z72): Kopf der Prüfkarte auf der Startseite. „Überall“: kein Satz, den sagt nur der
 *  Gaist in seiner Blase (Z73, `satzUnterwegs("vorschlag")`); „Nur bei Erklärungen“: derselbe Satz wie bisher als stiller
 *  Kartenkopf; „Keine Erklärungen“: sachlich wie bisher. Leere Liste heißt: die Karte trägt keinen Kopf. */
export function kopfVorschlag(praesenz: Praesenz): SatzTeil[] {
  if (praesenz === "ueberall") return [];
  return stimmeVorschlag(praesenz);
}

/** Präsenz-Hinweis (E13). */
export const PRAESENZ_HINWEIS = {
  satz: [{ text: "Ich melde mich oft. Wenn dir das zu viel ist, kannst du meine Hinweise reduzieren." }] as SatzTeil[],
  einstellen: "Gaist einstellen",
  verstanden: "Verstanden",
};

/** Offen-Block: Name, Zahl als Text und Unterzeile je Zelle (beide Fassungen). */
export type OffenZelle = { schluessel: "ueberfaellig" | "bald" | "pruefen" | "fixkosten"; name: string; zahl: number; zahlText: string; unter: string };

export function offenZellen(lage: OffenLage): OffenZelle[] {
  const fix = Math.floor(lage.fixkosten ?? 0);
  return [
    { schluessel: "ueberfaellig", name: "Überfällig", zahl: lage.ueberfaellig, zahlText: String(lage.ueberfaellig), unter: lage.ueberfaellig && lage.seit ? `seit ${tagMonat(lage.seit)}` : "nichts" },
    { schluessel: "bald", name: "Bald", zahl: lage.bald, zahlText: String(lage.bald), unter: lage.bald && lage.bis ? `bis ${wtTagMonat(lage.bis)}` : "nichts" },
    { schluessel: "pruefen", name: "Zu prüfen", zahl: lage.pruefen, zahlText: String(lage.pruefen), unter: lage.pruefen === 0 ? "nichts" : lage.pruefen === 1 ? "wartet auf dich" : "warten auf dich" },
    { schluessel: "fixkosten", name: "Fixkosten", zahl: fix, zahlText: `${fix} €`, unter: lage.fixkosten ? "im Monat" : "noch nichts eingetragen" },
  ];
}

/** Texte des Fristen-Moduls. */
export const FRISTEN_TEXTE = {
  titel: "Fristen",
  untertitel: "nächste 4 Wochen, Säulen in Euro",
  nochOffen: "noch offen",
  vorbei: "vorbei",
  heute: "Heute",
  legende: { ueberfaellig: "überfällig", bald: "bald", spaeter: "später", erledigt: "erledigt" },
  leer: "Keine Fristen in den nächsten 4 Wochen.",
  listeLeer: "Nichts offen.",
  alle: "Alle",
  alleLesetext: "Alle Fristen",
};

/** Achse einer Spalte: „vorbei“ oder „bis 22.10“ (b-HW23: künftiger Tag ohne Endpunkt) */
export function achse(bis: string | null): string {
  return bis ? `bis ${datumText(bis, "kurz")}` : FRISTEN_TEXTE.vorbei;
}

/** FW12 (D-436, F91/2): Fristwort wie iOS (`Format.fristart`) und wie die Vorlage R11 N1, das Hauptwort der Art. */
export const FRISTWORT: Record<string, string> = { zahlen: "Zahlung", widersprechen: "Widerspruch", kuendigen: "Kündigung", melden: "Meldung", sonstige: "Frist" };

/** FW12: rechte Spalte einer Fristzeile (R11 `.f-rechts`): oben der Betrag, ohne Betrag der Tag („21.10“, vorbei „14.10.“);
 *  darunter der Zustand mit seinem Wort: „seit gestern“, „seit 3 Tagen“, „heute“, „morgen“, „in 5 Tagen“, später mit Betrag „bis 27.10“ (ohne
 *  Betrag „in 27 Tagen“, der Tag steht dann oben), erledigt „bezahlt 14.10.2026“ bei einer Zahlung, sonst „erledigt 14.10.2026“.
 *  W82 Web-5h-N2b-2 (b-HW23, D-701): Formen aus datum.ts (künftig ohne Endpunkt, erledigt/bezahlt mit Jahr). Gerechnet in
 *  Europe/Berlin (`heute` aus `heuteBerlin`). `betragText` formatiert den Betrag (format.ts `betrag`). W82 Web-8 W8-2 (V:446,
 *  Wahl A): genau ein Tag überfällig heißt „seit gestern“, ab zwei Tagen „seit <n> Tagen“. */
export function fristRechts(
  f: { art: string; datum: string; betrag: string | null; erledigt: string | null },
  heute: string,
  betragText: (wert: string) => string,
): { oben: string; zustand: "ueberfaellig" | "bald" | "spaeter" | "erledigt"; text: string } {
  const oben = f.betrag ? betragText(f.betrag) : fristTag(f.datum, heute);
  if (f.erledigt) {
    const tag = /^\d{4}-\d{2}-\d{2}/.test(f.erledigt) ? eingangsTag(f.erledigt) : null;
    return { oben, zustand: "erledigt", text: erledigtText(f.art, tag) };
  }
  const t = tageBis(f.datum, heute);
  if (t < 0) return { oben, zustand: "ueberfaellig", text: t === -1 ? "seit gestern" : `seit ${-t} Tagen` };
  if (t <= 7) return { oben, zustand: "bald", text: t === 0 ? "heute" : t === 1 ? "morgen" : `in ${t} Tagen` };
  return { oben, zustand: "spaeter", text: f.betrag ? `bis ${datumText(f.datum, "kurz")}` : `in ${t} Tagen` };
}

/** Kalendertag eines Zeitpunkts in Europe/Berlin (erledigt kommt als Zeitpunkt): „…T22:30:00Z“ ist im Sommer (UTC+2) 00:30 des
 *  Folgetags, im Winter (UTC+1) 23:30 desselben Tags; die Rechnung über Europe/Berlin stimmt in beiden (W82 Web-11a W11A-5, H-6). */
function eingangsTag(zeitpunkt: string): string {
  const d = new Date(zeitpunkt);
  return Number.isNaN(d.getTime()) || zeitpunkt.length <= 10 ? zeitpunkt.slice(0, 10) : heuteBerlin(d);
}

/** Kopf der Liste: „4 offen“ und Lesetext „Fristen, 4 offen“. */
export function listeOffen(n: number): { text: string; lesetext: string } {
  return { text: `${n} offen`, lesetext: `Fristen, ${n} offen` };
}

/** Satz als Klartext (Tests, Vorlesen). */
export function klartext(teile: SatzTeil[]): string {
  return teile.map((t) => t.text).join("");
}

// ---------- Akten (Teil Web-3, plan-w82.md Abschnitt 6 „Akten, Sätze“; FW4: „Nach Art“ statt „A bis Z“) ----------

/** Sortierung der Akten (Merker `pg-akten-sortierung`, FW4): „Dringend zuerst“ oder „Nach Art“. */
export type AktenSortierung = "dringend" | "art";

/** Satz des Geists in den Akten nach der Sortierung. `oben` ist die dringendste offene Akte mit ihrem Rang nach E15
 *  (0 überfällig, 1 bald, 2 später, 3 Ruhe). „Nach Art“ (FW4, Vorlage R11 N2 `aktenBlaseSoll`): er schweigt und sitzt
 *  an seinem Platz in der Titelzeile, darum `null`; ohne offene Akte ebenso. */
export function satzAkten(sortierung: AktenSortierung, oben: { name: string; rang: number } | null): SatzTeil[] | null {
  if (sortierung === "art" || !oben) return null;
  const kopf: SatzTeil = { text: "Nach Dringlichkeit sortiert. " };
  if (oben.rang === 0) return [kopf, { text: `${oben.name} ist ` }, UEBERFAELLIG, { text: ", darum steht sie oben." }];
  if (oben.rang === 3) return [kopf, { text: "Gerade ist nichts fällig." }];
  return [kopf, { text: `${oben.name} ist als Nächstes fällig.` }];
}
